import { useMemo, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Select,
  Stack, Tab, Tabs, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchIcon from '@mui/icons-material/Search'
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Virtuoso } from 'react-virtuoso'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { MaintenanceRequest } from '@cmms/api-client'
import type { Site } from '@cmms/types'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { UserAvatar } from '../../components/UserAvatar'
import { useAuthStore } from '../../store/authStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { getStatusMeta } from '../work-orders/dummy'
import { useConfirm } from '../../components/ConfirmDialog'
import { TreeLocationPicker } from '../../components/TreeLocationPicker'
import { SearchableSelect } from '../../components/SearchableSelect'
import { formatSiteName } from '../../lib/siteDisplayName'

const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  submitted: { label: 'Pending', color: '#D97706', bg: '#FFFBEB', icon: ScheduleOutlinedIcon },
  approved:  { label: 'Approved', color: '#059669', bg: '#ECFDF5', icon: CheckCircleOutlinedIcon },
  declined:  { label: 'Declined', color: '#DC2626', bg: '#FEF2F2', icon: CancelOutlinedIcon },
  // The stored value is 'closed' (see requests_status_check), but it
  // only ever gets set once the request's linked work order/task
  // reaches 'resolved' — "Resolved" reads clearer here than "Closed",
  // which could be misread as "declined/shut without action".
  closed:    { label: 'Resolved', color: '#64748B', bg: '#F1F5F9', icon: CheckCircleOutlinedIcon },
}

const PRIORITY_META: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEF2F2' },
  high:     { label: 'High',     color: '#EA580C', bg: '#FFF7ED' },
  medium:   { label: 'Medium',   color: '#D97706', bg: '#FFFBEB' },
  low:      { label: 'Low',      color: '#059669', bg: '#ECFDF5' },
}

export function RequestsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { requestId } = useParams<{ requestId?: string }>()
  const selectedId = requestId ?? null
  const setSelectedId = (id: string | null) => navigate(id ? `/requests/${id}` : '/requests')
  const me = useAuthStore((s) => s.user)
  const { formatDate, formatDateShort } = useOrgSettings()
  const { can } = usePermissions()
  const [tab, setTab] = useState<'all' | 'submitted' | 'approved' | 'declined' | 'closed'>('all')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const requestsQ = useQuery({
    queryKey: ['requests'],
    queryFn: () => api.requests.list(),
  })
  const allRequests: MaintenanceRequest[] = Array.isArray(requestsQ.data) ? requestsQ.data : []

  const filtered = useMemo(() => {
    let list = allRequests
    if (tab !== 'all') list = list.filter((r) => r.status === tab)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((r) =>
        r.title.toLowerCase().includes(q)
        || r.submitted_by_name?.toLowerCase().includes(q)
        || r.requester_name?.toLowerCase().includes(q)
        || r.requester_email?.toLowerCase().includes(q)
        || r.description?.toLowerCase().includes(q))
    }
    return list
  }, [allRequests, tab, search])

  const counts = {
    all: allRequests.length,
    submitted: allRequests.filter((r) => r.status === 'submitted').length,
    approved: allRequests.filter((r) => r.status === 'approved').length,
    declined: allRequests.filter((r) => r.status === 'declined').length,
    closed: allRequests.filter((r) => r.status === 'closed').length,
  }

  const selected = selectedId ? allRequests.find((r) => r.id === selectedId) ?? null : null

  // Approve = flip status only. The server used to auto-spawn a work
  // order on approve; the UX now hands that off to the manual "New
  // Work Order" dialog so admins can pick assignees, attach
  // checklists, set due dates, etc. before the WO is created. The
  // navigate-with-prefill happens inside the onSuccess handler.
  const approveM = useMutation({
    mutationFn: (id: string) => api.requests.approve(id, { create_work_order: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['requests'] }); toast.success('Request approved') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const declineM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.requests.decline(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['requests'] }); toast.success('Request declined') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const [declineDialogId, setDeclineDialogId] = useState<string | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [linkPickerFor, setLinkPickerFor] = useState<MaintenanceRequest | null>(null)

  const tabSx = {
    minHeight: 38,
    '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600, px: 0, mr: 3, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } },
    '& .MuiTabs-indicator': { height: 2 },
  }

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedId(null) }} sx={{ ...tabSx, flex: 1 }}>
        <Tab value="all" label={`All (${counts.all})`} />
        <Tab value="submitted" label={`Pending (${counts.submitted})`} />
        <Tab value="approved" label={`Approved (${counts.approved})`} />
        <Tab value="declined" label={`Declined (${counts.declined})`} />
        <Tab value="closed" label={`Resolved (${counts.closed})`} />
      </Tabs>
      <TextField placeholder="Search requests..." value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: 220, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
    </Stack>
  )

  return (
    <PageShell
      title="Requests"
      subtitle="Maintenance requests from your team — review and convert to work orders."
      toolbar={toolbar}
      scrollContent={false}
      actions={
        <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setCreateOpen(true)}
          sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}>
          New request
        </Button>
      }
    >
      {requestsQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>No requests</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
            {tab === 'all' ? 'Submit a request to report an issue or ask for maintenance.'
              : `No ${tab === 'closed' ? 'resolved' : tab} requests found.`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Left — List. Virtualized via react-virtuoso — with
              thousands of requests, mounting every row as a real DOM
              node made the list slow to reconcile on every selection
              change (same fix as AssetList/KanbanView). */}
          <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0, borderRight: { md: '1px solid' }, borderColor: 'divider',
            display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Virtuoso
              style={{ height: '100%' }}
              data={filtered}
              increaseViewportBy={200}
              itemContent={(_idx, r) => {
                const st = STATUS_META[r.status] ?? STATUS_META.submitted!
                const pri = PRIORITY_META[r.priority] ?? PRIORITY_META.medium!
                const isActive = selectedId === r.id
                return (
                  <Box onClick={() => setSelectedId(r.id)}
                    sx={{
                      px: 2.5, py: 2, cursor: 'pointer', position: 'relative',
                      borderBottom: '1px solid', borderColor: 'divider',
                      bgcolor: isActive ? (t) => alpha(t.palette.primary.main, 0.04) : 'transparent',
                      transition: 'background 120ms',
                      '&:hover': { bgcolor: isActive ? undefined : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                      '&::before': isActive ? {
                        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main',
                      } : undefined,
                    }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: pri.color, mt: 0.75, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, mb: 0.35 }} noWrap>{r.title}</Typography>
                        {r.description && (
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mb: 0.75,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {r.description}
                          </Typography>
                        )}
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          {(() => {
                            // Same fallback chain as the detail panel —
                            // external-source rows (email intake or public
                            // QR scans) use requester_name/email instead of
                            // the (null) submitted_by user.
                            const isEmail    = r.source === 'email'     && !r.submitted_by_name
                            const isPublicQr = r.source === 'public_qr' && !r.submitted_by_name
                            const isExternal = isEmail || isPublicQr
                            const listName = isExternal
                              ? (r.requester_name || r.requester_email
                                  || (isPublicQr ? 'Public QR' : 'Email request'))
                              : r.submitted_by_name
                            return (
                              <>
                                <UserAvatar name={listName ?? undefined}
                                  avatarUrl={isExternal ? null : r.submitted_by_avatar}
                                  size={18} sx={{ bgcolor: '#0D9488' }} />
                                <Typography sx={{ fontSize: 11, color: 'text.secondary',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                  {listName ?? '—'}
                                </Typography>
                                {isPublicQr ? (
                                  <Chip label="QR" size="small"
                                    sx={{ height: 15, fontSize: 8.5, fontWeight: 700,
                                      bgcolor: '#3B82F614', color: '#1D4ED8',
                                      '& .MuiChip-label': { px: 0.6 } }} />
                                ) : isEmail && (
                                  <Chip label="email" size="small"
                                    sx={{ height: 15, fontSize: 8.5, fontWeight: 700,
                                      bgcolor: '#F59E0B14', color: '#B45309',
                                      '& .MuiChip-label': { px: 0.6 } }} />
                                )}
                              </>
                            )
                          })()}
                          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{timeAgo(r.created_at, formatDateShort)}</Typography>
                        </Stack>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                        <Chip label={st.label} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: st.bg, color: st.color }} />
                        {/* Linked WO/task's own status — distinct from the
                            request's own status above (e.g. a request can
                            read "Approved" while the task it became is
                            already "In Progress" or "Completed"). */}
                        {r.work_order_id && r.work_order_status && (() => {
                          const wst = getStatusMeta(r.work_order_status, r.work_order_kind === 'task')
                          return (
                            <Chip label={wst.label} size="small"
                              sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: wst.bg, color: wst.color }} />
                          )
                        })()}
                        <Chip label={pri.label} size="small"
                          sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: `${pri.color}14`, color: pri.color }} />
                      </Stack>
                    </Stack>
                  </Box>
                )
              }}
            />
          </Box>

          {/* Right — Detail */}
          <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
            <AnimatePresence mode="wait">
              {selected ? (
                <motion.div key={selected.id} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                  <RequestDetail
                    request={selected}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                    canManage={can('work_orders:create')}
                    onApprove={async () => {
                      // Approve only. We used to auto-navigate straight
                      // to the WO create form so the admin could pick
                      // assignee / dates / attachments — but users found
                      // it confusing when they hadn't decided yet
                      // whether the request should become a task or a
                      // work order. Now approve stays on this page and
                      // reveals the "Create task" / "Create work order"
                      // / "Link existing" buttons for the admin to
                      // choose from at their own pace.
                      await approveM.mutateAsync(selected.id)
                    }}
                    onDecline={() => { setDeclineDialogId(selected.id); setDeclineReason('') }}
                    onLinkExisting={() => setLinkPickerFor(selected)}
                    onCreateTicket={(kind) => {
                      if (selected.work_order_id) {
                        // The linked record's OWN kind decides the route
                        // — was hardcoded to /work-orders regardless, so
                        // a request that had converted into a task (not
                        // a work order) linked to a real id under the
                        // wrong module. WorkOrdersPage scopes its list to
                        // one kind, so the id-lookup there silently
                        // failed to match and left whatever was
                        // previously selected on screen instead — the
                        // "linked work order doesn't match" symptom.
                        const base = selected.work_order_kind === 'task' ? '/tasks' : '/work-orders'
                        navigate(`${base}/${selected.work_order_id}`)
                      } else {
                        // Each kind now lives in its own module — task
                        // converts land on /tasks, work-order converts
                        // land on /work-orders. Each page forces its
                        // own kind server-side, so no `kind=` param.
                        const k = kind ?? 'work_order'
                        const base = k === 'task' ? '/tasks' : '/work-orders'
                        navigate(`${base}?new=1&request_title=${encodeURIComponent(selected.title)}&request_desc=${encodeURIComponent(selected.description ?? '')}&request_id=${selected.id}&site_id=${selected.site_id ?? ''}&location_id=${selected.location_id ?? ''}&assign_reporter=${selected.submitted_by ?? ''}`)
                      }
                    }}
                    approvePending={approveM.isPending}
                    declinePending={declineM.isPending}
                    currentUserId={me?.id ?? null}
                  />
                </motion.div>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 8 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', opacity: 0.3, mb: 1.5 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>Select a request</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>Choose a request from the list to view details</Typography>
                </Box>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      )}

      {createOpen && <CreateRequestDialog onClose={() => setCreateOpen(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ['requests'] }); setCreateOpen(false) }} />}

      {linkPickerFor && (
        <LinkExistingWorkOrderDialog
          request={linkPickerFor}
          onClose={() => setLinkPickerFor(null)}
          onLinked={() => { qc.invalidateQueries({ queryKey: ['requests'] }); setLinkPickerFor(null) }}
        />
      )}

      {/* Decline dialog */}
      <Dialog open={!!declineDialogId} onClose={() => setDeclineDialogId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Decline request</DialogTitle>
        <DialogContent>
          <TextField label="Reason (optional)" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
            size="small" fullWidth multiline minRows={2} sx={{ mt: 1 }}
            placeholder="Let them know why this was declined..." />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeclineDialogId(null)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={declineM.isPending}
            onClick={() => { if (declineDialogId) { declineM.mutate({ id: declineDialogId, reason: declineReason }); setDeclineDialogId(null) } }}>
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  )
}

// ─── Request detail panel ──────────────────────────────────────────────────

function RequestDetail({ request: r, formatDate, formatDateShort, canManage, onApprove, onDecline, onCreateTicket, onLinkExisting, approvePending, declinePending, currentUserId }: {
  request: MaintenanceRequest; formatDate: (d: string) => string; formatDateShort: (d: Date | string) => string; canManage: boolean
  onApprove: () => void; onDecline: () => void; onCreateTicket: (kind?: 'work_order' | 'task') => void; onLinkExisting: () => void
  approvePending: boolean; declinePending: boolean; currentUserId: string | null
}) {
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const st = STATUS_META[r.status] ?? STATUS_META.submitted!
  const pri = PRIORITY_META[r.priority] ?? PRIORITY_META.medium!
  const StIcon = st.icon
  const canEdit = r.status === 'submitted' && r.submitted_by === currentUserId

  return (
    <Box sx={{ p: { xs: 2.5, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25, mb: 1.25 }}>
            {r.title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip icon={<StIcon sx={{ fontSize: 14 }} />} label={st.label} size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 700, bgcolor: st.bg, color: st.color, '& .MuiChip-icon': { color: 'inherit' } }} />
            <Chip label={pri.label} size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 700, bgcolor: pri.bg, color: pri.color }} />
            {r.metadata?.iot_alarm && (
              <Tooltip
                title={(() => {
                  const a = r.metadata!.iot_alarm!
                  const pn = a.point_name ?? a.arcos_point_id ?? 'point'
                  const v  = a.value_at_trigger == null ? null
                    : `${a.value_at_trigger}${a.unit ? ' ' + a.unit : ''}`
                  const lim = a.limit == null ? null
                    : `${a.limit}${a.unit ? ' ' + a.unit : ''}`
                  return v && lim ? `${pn} · ${v} (limit ${lim})` : pn
                })()}
                arrow
              >
                <Chip
                  icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Arcos alarm"
                  size="small"
                  sx={{
                    height: 26, fontSize: 11.5, fontWeight: 700,
                    bgcolor: '#FEF3C7', color: '#92400E',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
        {canEdit && (
          <Tooltip title="Edit request" arrow>
            <IconButton size="small" onClick={() => setEditOpen(true)}
              sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {editOpen && (
        <EditRequestDialog request={r} onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); qc.invalidateQueries({ queryKey: ['requests'] }) }} />
      )}

      {/* Description. Public-QR requests concatenate the item
          description, "Requested via public QR by …" line, location,
          and (optionally) the requester's own free-text note into
          `description`. Split those apart so the free-text note —
          the actual "why this was requested" — lands in a warning-
          styled callout that reads first, above the generic block.
          Falls back to the older "Reporter's note:" marker for rows
          created before the terminology rename. */}
      {r.description && (() => {
        const markers = ["Requester's note:", "Reporter's note:"]
        let idx = -1
        let markerLen = 0
        for (const m of markers) {
          const found = r.description.indexOf(m)
          if (found >= 0) { idx = found; markerLen = m.length; break }
        }
        const before = idx >= 0 ? r.description.slice(0, idx).trimEnd() : r.description
        const note   = idx >= 0 ? r.description.slice(idx + markerLen).trim() : ''
        return (
          <>
            {note && (
              <Box sx={{ mb: 2, p: 2.25, borderRadius: 2, border: '1px solid #FCD34D',
                bgcolor: '#FFFBEB' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: '#B45309', mb: 0.75 }}>
                  Requester's note
                </Typography>
                <Typography sx={{ fontSize: 14, color: '#78350F', lineHeight: 1.65, whiteSpace: 'pre-wrap',
                  fontWeight: 500 }}>
                  {note}
                </Typography>
              </Box>
            )}
            {before && (
              <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'text.primary', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {before}
                </Typography>
              </Box>
            )}
          </>
        )
      })()}

      {r.metadata?.iot_alarm && (
        <SourceTelemetryCard alarm={r.metadata.iot_alarm} formatDate={formatDate} />
      )}

      {/* Reporter photo gallery — visible when the public-QR intake
          included any attachments. Click a thumbnail to open the
          full-size image in a new tab; no lightbox to keep the
          detail view lean. */}
      {r.submitter_image_urls && r.submitter_image_urls.length > 0 && (
        <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'text.secondary', mb: 1.25 }}>
            Photos ({r.submitter_image_urls.length})
          </Typography>
          <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', gap: 1.25 }} useFlexGap>
            {r.submitter_image_urls.map((url, i) => (
              <Box key={url}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 120, height: 120, borderRadius: 2, overflow: 'hidden',
                  display: 'block',
                  border: '1px solid', borderColor: 'divider',
                  cursor: 'zoom-in',
                  transition: 'transform 120ms ease, border-color 120ms ease',
                  '&:hover': { transform: 'scale(1.03)', borderColor: 'primary.main' },
                }}>
                <Box component="img"
                  src={url}
                  alt={`Photo ${i + 1}`}
                  loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Info cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        <InfoCard icon={PersonOutlineIcon} label="Submitted by" color="#0D9488">
          {(() => {
            // External-source requests (email intake or public location-QR
            // "Report an issue" scans) don't have an internal user. Fall
            // back to requester_email / _name + a source badge so the
            // panel doesn't render as an anonymous placeholder.
            const isEmail    = r.source === 'email'     && !r.submitted_by_name
            const isPublicQr = r.source === 'public_qr' && !r.submitted_by_name
            const isExternal = isEmail || isPublicQr
            const displayName = isExternal
              ? (r.requester_name || r.requester_email || '—')
              : (r.submitted_by_name ?? '—')
            const displayEmail = isExternal ? r.requester_email : r.submitted_by_email
            const avatarName = isExternal
              ? (r.requester_name || r.requester_email || undefined)
              : r.submitted_by_name
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <UserAvatar name={avatarName} avatarUrl={isExternal ? null : r.submitted_by_avatar}
                  size={28} sx={{ bgcolor: '#0D9488' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName}
                    </Typography>
                    {isPublicQr ? (
                      <Chip label="Via QR" size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#3B82F614', color: '#1D4ED8' }} />
                    ) : isEmail ? (
                      <Chip label="Via email" size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#F59E0B14', color: '#B45309' }} />
                    ) : r.submitted_by_role ? (
                      <Chip label={r.submitted_by_role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#0D948814', color: '#0D9488' }} />
                    ) : null}
                  </Stack>
                  {displayEmail && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayEmail}
                    </Typography>
                  )}
                </Box>
              </Stack>
            )
          })()}
        </InfoCard>

        <InfoCard icon={CalendarTodayOutlinedIcon} label="Submitted on" color="#3B82F6">
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{formatDate(r.created_at)}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{timeAgo(r.created_at, formatDateShort)}</Typography>
        </InfoCard>

        {(r.site_name || r.location_name) && (
          <InfoCard icon={LocationOnOutlinedIcon} label="Location" color="#7C3AED">
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{r.site_name}</Typography>
            {r.location_name && <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{r.location_name}</Typography>}
          </InfoCard>
        )}

        <InfoCard icon={PriorityHighOutlinedIcon} label="Priority" color={pri.color}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: pri.color }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{r.priority}</Typography>
          </Stack>
        </InfoCard>
      </Box>

      {/* Review info */}
      {r.reviewed_by_name && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid',
          borderColor: r.status === 'approved' ? '#BBF7D0' : '#FECACA',
          bgcolor: r.status === 'approved' ? '#F0FDF4' : '#FEF2F2' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <UserAvatar name={r.reviewed_by_name} avatarUrl={r.reviewed_by_avatar} size={32}
              sx={{ bgcolor: r.status === 'approved' ? '#059669' : '#DC2626' }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: r.status === 'approved' ? '#166534' : '#991B1B' }}>
                  {r.status === 'approved' ? 'Approved' : 'Declined'} by {r.reviewed_by_name}
                </Typography>
                {r.reviewed_by_role && (
                  <Chip label={r.reviewed_by_role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                    sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: r.status === 'approved' ? '#DCFCE7' : '#FEE2E2',
                      color: r.status === 'approved' ? '#166534' : '#991B1B' }} />
                )}
              </Stack>
              {r.reviewed_at && <Typography sx={{ fontSize: 11.5, color: r.status === 'approved' ? '#15803D' : '#B91C1C', opacity: 0.7 }}>{formatDate(r.reviewed_at)}</Typography>}
            </Box>
          </Stack>
          {r.decline_reason && (
            <Typography sx={{ fontSize: 12.5, color: '#991B1B', mt: 1.5, pl: 5.5, fontStyle: 'italic', lineHeight: 1.5 }}>
              "{r.decline_reason}"
            </Typography>
          )}
        </Box>
      )}

      {/* Actions — submitted requests can only be approved or declined.
          Work-order / task creation is gated behind approval so admins
          consciously confirm the request before turning it into work. */}
      {r.status === 'submitted' && canManage && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" color="success" disabled={approvePending || declinePending}
              startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={onApprove}
              sx={{ fontSize: 13, fontWeight: 700, borderRadius: 2, flex: 1, py: 1.25 }}>
              {approvePending ? 'Approving...' : 'Approve'}
            </Button>
            <Button variant="outlined" color="error" disabled={approvePending || declinePending}
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={onDecline}
              sx={{ fontSize: 13, fontWeight: 700, borderRadius: 2, flex: 1, py: 1.25 }}>
              {declinePending ? 'Declining...' : 'Decline'}
            </Button>
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
            Approve the request first to create a work order or task.
          </Typography>
        </Stack>
      )}

      {r.status === 'approved' && !r.work_order_id && canManage && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="inherit"
              sx={{ fontSize: 12.5, fontWeight: 700, borderRadius: 2, flex: 1, py: 1.25,
                bgcolor: '#1D4ED8', color: 'white', '&:hover': { bgcolor: '#1E40AF' } }}
              onClick={() => onCreateTicket('task')}>
              Create task
            </Button>
            <Button variant="contained"
              sx={{ fontSize: 12.5, fontWeight: 700, borderRadius: 2, flex: 1, py: 1.25 }}
              onClick={() => onCreateTicket('work_order')}>
              Create work order
            </Button>
          </Stack>
          <Button variant="text" fullWidth size="small"
            onClick={onLinkExisting}
            sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'none' }}>
            Already created? Link an existing work order
          </Button>
        </Stack>
      )}

      {/* Was gated on `r.status === 'approved'` too — once a request
          auto-closes (its linked work order/task reached resolved),
          this whole block vanished even though `work_order_id` is
          still set, so there was no way left to find what it's
          linked to. The link is permanent once made, regardless of
          the request's own status, so this only needs work_order_id. */}
      {r.work_order_id && (
        <Stack spacing={1}>
          {r.work_order_status && (() => {
            const wst = getStatusMeta(r.work_order_status, r.work_order_kind === 'task')
            return (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>
                  {r.work_order_code ?? (r.work_order_kind === 'task' ? 'Task' : 'Work order')} status
                </Typography>
                <Chip label={wst.label} size="small"
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: wst.bg, color: wst.color }} />
              </Stack>
            )
          })()}
          <Button variant="outlined" fullWidth
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => onCreateTicket()}
            sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, py: 1.25, color: '#3B82F6', borderColor: '#3B82F6' }}>
            View linked {r.work_order_kind === 'task' ? 'task' : 'work order'}
          </Button>
        </Stack>
      )}
    </Box>
  )
}

// ─── Source telemetry (Arcos alarm context) ────────────────────────────────

function SourceTelemetryCard({ alarm, formatDate }: {
  alarm: NonNullable<MaintenanceRequest['metadata']>['iot_alarm']
  formatDate: (d: string) => string
}) {
  if (!alarm) return null
  const arcosBase = (import.meta.env.VITE_ARCOS_PUBLIC_URL ?? '').replace(/\/+$/, '')
  // Only render the deep-link button if we know an Arcos URL — without
  // it we'd ship a useless half-built href. The folder/point id still
  // shows in plain text so a user can copy it manually.
  const folderHref = arcosBase && alarm.arcos_folder_id
    ? `${arcosBase}/assets/${alarm.arcos_folder_id}` : null

  const triggerStr = alarm.value_at_trigger == null ? '—'
    : `${alarm.value_at_trigger}${alarm.unit ? ' ' + alarm.unit : ''}`
  const limitStr = alarm.limit == null ? null
    : `limit ${alarm.limit}${alarm.unit ? ' ' + alarm.unit : ''}`

  return (
    <Box sx={{
      mb: 3, p: 2.5, borderRadius: 2,
      border: '1px solid #FCD34D',
      bgcolor: (t) => t.palette.mode === 'light' ? '#FFFBEB' : 'rgba(252, 211, 77, 0.06)',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SensorsOutlinedIcon sx={{ fontSize: 16, color: '#92400E' }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E' }}>
            Source telemetry
          </Typography>
        </Stack>
        {folderHref && (
          <Button
            component="a"
            href={folderHref}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: 12, fontWeight: 600, textTransform: 'none',
              color: '#92400E', borderColor: '#FCD34D',
              '&:hover': { borderColor: '#F59E0B', bgcolor: '#FEF3C7' },
            }}
          >
            Open in Arcos
          </Button>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        <TelemetryKv label="Point" value={alarm.point_name ?? alarm.arcos_point_id ?? '—'} />
        <TelemetryKv label="Alarm type" value={alarm.type ?? '—'} />
        <TelemetryKv label="Value at trigger" value={triggerStr} mono />
        {limitStr && <TelemetryKv label="Threshold" value={limitStr} mono />}
        {alarm.raised_at && <TelemetryKv label="Raised at" value={formatDate(alarm.raised_at)} />}
        {alarm.cleared_at && <TelemetryKv label="Cleared at" value={formatDate(alarm.cleared_at)} />}
      </Box>

      {Array.isArray(alarm.tags) && alarm.tags.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {alarm.tags.map((t) => (
            <Chip key={t} label={t} size="small"
              sx={{ height: 20, fontSize: 10.5, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function TelemetryKv({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E', opacity: 0.7 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 13, fontWeight: 600, color: 'text.primary', mt: 0.25,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
      }}>
        {value}
      </Typography>
    </Box>
  )
}

function InfoCard({ icon: Icon, label, color, children }: { icon: React.ElementType; label: string; color: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 14, color }} />
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      {children}
    </Box>
  )
}

function timeAgo(date: string, formatDateShort: (d: Date | string) => string) {
  const ms = Date.now() - new Date(date).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return formatDateShort(date)
}

// ─── Link existing work order dialog ────────────────────────────────────────

function LinkExistingWorkOrderDialog({ request, onClose, onLinked }: {
  request: MaintenanceRequest; onClose: () => void; onLinked: () => void
}) {
  const [search, setSearch] = useState('')
  const [pickedId, setPickedId] = useState<string | null>(null)

  const wosQ = useQuery({
    queryKey: ['work-orders', { site_id: request.site_id ?? undefined }],
    queryFn: () => api.workOrders.list(request.site_id ? { site_id: request.site_id } : undefined),
  })
  const wos = Array.isArray(wosQ.data) ? wosQ.data : []
  const filtered = search.trim()
    ? wos.filter((w: any) => (w.title?.toLowerCase().includes(search.toLowerCase())
        || w.wo_code?.toLowerCase().includes(search.toLowerCase())))
    : wos

  const linkM = useMutation({
    mutationFn: (workOrderId: string) => api.requests.linkWorkOrder(request.id, workOrderId),
    onSuccess: () => { toast.success('Work order linked'); onLinked() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to link'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Link existing work order</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
          Pick the work order that was already created for this request.
          {request.site_id ? ' Showing only work orders at the same site.' : ''}
        </Typography>
        <TextField placeholder="Search by title or WO code..." value={search}
          onChange={(e) => setSearch(e.target.value)} size="small" fullWidth autoFocus
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }} />
        <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          {wosQ.isLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ py: 4, textAlign: 'center', fontSize: 12.5, color: 'text.secondary' }}>
              No work orders found.
            </Typography>
          ) : filtered.map((wo: any) => {
            const isPicked = pickedId === wo.id
            return (
              <Box key={wo.id} onClick={() => setPickedId(wo.id)}
                sx={{
                  px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: isPicked ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
                  '&:hover': { bgcolor: (t) => isPicked ? alpha(t.palette.primary.main, 0.12) : 'action.hover' },
                  '&:last-of-type': { borderBottom: 'none' },
                }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ width: 18, height: 18, borderRadius: 99, border: '2px solid',
                    borderColor: isPicked ? 'primary.main' : 'divider',
                    bgcolor: isPicked ? 'primary.main' : 'transparent', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isPicked && <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: '#fff' }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{wo.title}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }} noWrap>
                      {wo.wo_code} · {wo.status}{wo.site_name ? ` · ${wo.site_name}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!pickedId || linkM.isPending}
          onClick={() => pickedId && linkM.mutate(pickedId)}>
          {linkM.isPending ? 'Linking…' : 'Link work order'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Create request dialog ─────────────────────────────────────────────────

function CreateRequestDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  const [siteId, setSiteId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [locationName, setLocationName] = useState('')

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const createM = useMutation({
    mutationFn: () => api.requests.create({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      site_id: siteId || undefined,
      location_id: locationId || undefined,
    }),
    onSuccess: () => { toast.success('Request submitted!'); onCreated() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 99, bgcolor: '#D9770614', color: '#D97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Report an issue</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Tell us what needs fixing — our maintenance team will review it.</Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        <Stack spacing={2.5}>
          <TextField label="What's the problem?" required value={title} onChange={(e) => setTitle(e.target.value)}
            size="small" fullWidth autoFocus placeholder="e.g. AC not working in Room 204"
            helperText="A short summary of the issue" />

          <TextField label="More details" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={3}
            placeholder="Describe the issue — when did it start? How urgent is it? Is it affecting work?"
            helperText="The more detail you provide, the faster we can help" />

          <Divider />

          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
            Priority & Location
          </Typography>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>How urgent is this?</Typography>
            <Stack direction="row" spacing={1}>
              {(['low', 'medium', 'high', 'critical'] as const).map((p) => {
                const m = PRIORITY_META[p]!
                const isSelected = priority === p
                return (
                  <Box key={p} onClick={() => setPriority(p)}
                    sx={{ flex: 1, py: 1.25, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
                      border: '1.5px solid', borderColor: isSelected ? m.color : 'divider',
                      bgcolor: isSelected ? m.bg : 'transparent',
                      transition: 'all 120ms', '&:hover': { borderColor: m.color } }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: m.color, mx: 'auto', mb: 0.5 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? m.color : 'text.secondary', textTransform: 'capitalize' }}>
                      {p}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Where is the issue?</Typography>
            <SearchableSelect
              value={siteId}
              onChange={(sid) => { setSiteId(sid); setLocationId(''); setLocationName('') }}
              options={sites}
              getValue={(s) => s.id}
              getLabel={formatSiteName}
              placeholder="Select site"
              renderOption={(s) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
                </Stack>
              )}
            />
          </Box>

          {siteId && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Specific location (optional)</Typography>
              <TreeLocationPicker siteId={siteId} value={locationId} displayName={locationName || undefined}
                onChange={(id, name) => { setLocationId(id); setLocationName(name) }}
                onClear={() => { setLocationId(''); setLocationName('') }} />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <Box sx={{ px: 3.5, py: 2.5 }}>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button onClick={onClose} sx={{ color: 'text.secondary', fontSize: 13 }}>Cancel</Button>
          <Button variant="contained" disabled={!title.trim() || createM.isPending} onClick={() => createM.mutate()}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 13, px: 4 }}>
            {createM.isPending ? 'Submitting...' : 'Submit request'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}

// ─── Edit request dialog ──────────────────────────────────────────────────

function EditRequestDialog({ request: r, onClose, onSaved }: { request: MaintenanceRequest; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(r.title)
  const [description, setDescription] = useState(r.description ?? '')
  const [priority, setPriority] = useState(r.priority)
  const [siteId, setSiteId] = useState(r.site_id ?? '')
  const [locationId, setLocationId] = useState(r.location_id ?? '')
  const [locationName, setLocationName] = useState(r.location_name ?? '')

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const updateM = useMutation({
    // This dialog is edit-only, so a cleared field must send an
    // explicit `null` — omitting it (`undefined`) means "leave
    // unchanged" to the backend's sparse update, so the old value
    // would silently survive a "successful" save.
    mutationFn: () => api.requests.update(r.id, {
      title: title.trim(),
      description: description.trim() || null,
      priority,
      site_id: siteId || null,
      location_id: locationId || null,
    }),
    onSuccess: () => { toast.success('Request updated'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit request</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="What's the problem?" required value={title} onChange={(e) => setTitle(e.target.value)}
            size="small" fullWidth autoFocus />
          <TextField label="More details" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={3} />

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>How urgent is this?</Typography>
            <Stack direction="row" spacing={1}>
              {(['low', 'medium', 'high', 'critical'] as const).map((p) => {
                const m = PRIORITY_META[p]!
                const isSelected = priority === p
                return (
                  <Box key={p} onClick={() => setPriority(p)}
                    sx={{ flex: 1, py: 1.25, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
                      border: '1.5px solid', borderColor: isSelected ? m.color : 'divider',
                      bgcolor: isSelected ? m.bg : 'transparent',
                      transition: 'all 120ms', '&:hover': { borderColor: m.color } }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: m.color, mx: 'auto', mb: 0.5 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? m.color : 'text.secondary', textTransform: 'capitalize' }}>
                      {p}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Site</Typography>
            <SearchableSelect
              value={siteId}
              onChange={(sid) => { setSiteId(sid); setLocationId(''); setLocationName('') }}
              options={sites}
              getValue={(s) => s.id}
              getLabel={formatSiteName}
              placeholder="Select site"
              renderOption={(s) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
                </Stack>
              )}
            />
          </Box>

          {siteId && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Specific location</Typography>
              <TreeLocationPicker siteId={siteId} value={locationId} displayName={locationName || undefined}
                onChange={(id, name) => { setLocationId(id); setLocationName(name) }}
                onClear={() => { setLocationId(''); setLocationName('') }} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!title.trim() || updateM.isPending} onClick={() => updateM.mutate()}
          sx={{ borderRadius: 2, fontWeight: 700 }}>
          {updateM.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
